<script setup lang="ts">
import { useFileImage } from '@/composables/files/useFileImage';
import { STUDENT_AVATAR_ICON_OPTIONS } from '@/constants';
import UserAvatarFallback from '@/core/UserAvatarFallback.vue';
import { IGenericUser } from '@/interfaces';
import { EIcon } from 'common/enum/misc.enum';
import { useObjectUrl } from '@vueuse/core';
import { computed, shallowRef, toRefs, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    user: IGenericUser | null;
    theme?: 'chart' | 'default';
  }>(),
  {
    theme: undefined,
  },
);
const { user, theme } = toRefs(props);

const iconComponent = shallowRef(null);

const avatar = computed(() => user.value?.avatar ?? '');
const localAvatarPictureUrl = useObjectUrl(() => user.value?.avatarPictureFile);
const avatarPictureId = computed(() => user.value?.avatarPictureId ?? null);
const avatarPictureSource = computed(() => localAvatarPictureUrl.value ?? avatarPictureId.value);
const { url: avatarPictureUrl } = useFileImage(avatarPictureSource);

const isUserAvatarIcon = computed(() => STUDENT_AVATAR_ICON_OPTIONS.includes(avatar.value as EIcon));

watch(
  avatar,
  async (newAvatar) => {
    if (newAvatar && isUserAvatarIcon.value) {
      iconComponent.value = (await import(`@/assets/icons/${newAvatar}Icon.vue`)).default;
    } else {
      iconComponent.value = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <img
    v-if="avatarPictureUrl"
    :src="avatarPictureUrl"
    alt="User Avatar"
    class="rounded-full bg-surface-tint object-cover shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  />
  <UserAvatarFallback v-else-if="!avatar" :user="user" :theme="theme" />
  <img
    v-else-if="!isUserAvatarIcon"
    :src="`/assets/avatars/${avatar}.png`"
    alt="User Avatar"
    class="rounded-full bg-surface-tint shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  />
  <span
    v-else
    class="size-avatar rounded-full bg-surface-tint flex items-center justify-center shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  >
    <component :is="iconComponent" class="size-[60%] text-tint" />
  </span>
</template>
